import { AttractLoop, Configuration } from 'store/configurations';
import { ClientIntegration } from 'store/media';

export enum Selections {
  EVENT_IMAGES = 'eventImages',
  VENUE_IMAGES = 'venueImages',
  GENRE_IMAGES = 'genreImages',
  ATTRACT = 'attract',
  OTHER_IMAGES = 'otherImages'
}

export enum MediaTypes {
  EVENT = 'event',
  VENUE = 'venue',
  GENRE = 'genre',
  ATTRACT_IMAGE = 'attract-image',
  ATTRACT_VIDEO = 'attract-video',
  INACTIVE_IMAGE = 'inactive-image',
  SPLASH_IMAGE = 'splash-image',
  LOGO_IMAGE = 'logo-image',
  LOGO_IMAGE_LARGE = 'logo-image-large',
  BACKGROUND_IMAGE = 'background-image',
  LINKED_CONTENT = 'attract-url',
  PROMO_CODE_IMAGE = 'promo-code-image',
  KIOSK_GRAPHIC = 'kiosk-graphic'
}

// Kiosk graphic slot keys, stored as the media record name (must match kis-kiosk)
export enum KioskGraphics {
  START_PURCHASE = 'start-purchase',
  START_DONATIONS = 'start-donations',
  START_WILL_CALL = 'start-will-call',
  START_INFO = 'start-info',
  START_SOUVENIR = 'start-souvenir',
  START_CITYPASS = 'start-citypass',
  DELIVERY_EMAIL = 'delivery-email',
  DELIVERY_SMS = 'delivery-sms',
  DELIVERY_PRINT = 'delivery-print',
  LOOKUP_SCAN = 'lookup-scan',
  LOOKUP_MANUAL = 'lookup-manual',
  EMPTY_NO_EVENTS = 'empty-no-events',
  EMPTY_NO_RESULTS = 'empty-no-results',
  PAYMENT_SCREEN = 'payment-screen',
  IMAGE_FALLBACK = 'image-fallback'
}

export interface MediaType {
  name:
    | 'Slideshow'
    | 'Video'
    | 'Inactive Page Image'
    | 'Splash Page Image'
    | 'Client Logo Image - Small'
    | 'Client Logo Image - Large'
    | 'Background Image'
    | 'Linked Content'
    | 'Promo Code Images'
    | 'Kiosk Graphics';
  value:
    | 'attract-image'
    | 'attract-video'
    | 'inactive-image'
    | 'splash-image'
    | 'logo-image'
    | 'logo-image-large'
    | 'background-image'
    | 'attract-url'
    | 'promo-code-image'
    | 'kiosk-graphic';
  path?:
    | 'inactive_images'
    | 'splash_images'
    | 'logo_images'
    | 'background_images'
    | 'promo_code_images'
    | 'kiosk_graphics';
}

export interface MediaResponse {
  clientId: string;
  id: string;
  name: string;
  s3Name: string;
  priority?: number;
  imageName?: string;
}

export interface KioskGraphicSlot {
  key: KioskGraphics;
  label: string;
  defaultSrc: string;
  defaultFileName: string;
  recommendedResolution: string;
}

export interface ResourceRow {
  label: string;
  resourceName: string;
  mediaData?: MediaResponse;
  id: string;
  defaultGraphic?: KioskGraphicSlot;
}

export interface MediaConfigurationProps {
  configuration: Configuration;
}

export interface CIMediaProps {
  environment: ClientIntegration | null;
}

export interface ControlsProps {
  configuration?: Configuration;
  mediaCount: number;
  mediaType: MediaTypes;
  onAddClick?: () => void;
  onMakeActiveClick?: () => void;
  onSetTimeClick?: () => void;
}

export interface ImagePreviewProps {
  fileUrl: string;
  width: number;
  halfPreview?: boolean;
  imageType?: string;
}

export interface LinkedContentTableProps {
  linkedContentUrl?: string;
  onDeleteClick: () => void;
  onEditClick: () => void;
}

export interface MediaTableProps {
  mediaRecords: MediaResponse[];
  mediaType: MediaTypes;
  onDeleteClick?: (mediaRecord: MediaResponse) => void;
  onDownloadClick?: (mediaRecord: MediaResponse) => void;
  onEditClick?: (mediaRecord?: MediaResponse, promoCode?: string) => void;
  onPreviewClick?: (mediaSource: string) => void;
  onOrderChange?: (order: string[]) => void;
  promoCodes?: string[];
  kioskGraphics?: KioskGraphicSlot[];
}

export interface MediaTableRowProps {
  enableDragAndDrop?: boolean;
  id: string;
  mediaSource?: string;
  label?: string;
  mediaRecord?: MediaResponse;
  mediaTypeLabel: string;
  onDeleteClick?: () => void;
  onDownloadClick?: () => void;
  onEditClick?: () => void;
  onPreviewClick?: () => void;
}

export interface RequirementsProps {
  mediaType: MediaTypes;
  // kiosk graphic slot key, since each slot has its own recommended resolution
  resourceName?: string;
}

export interface TransitionTimeOverlayProps {
  configuration: Configuration;
  onClose: () => void;
  onSave: (payload: Partial<AttractLoop>) => void;
}

export interface ImageUploadOverlayTypes {
  onCancel: () => void;
  onSuccess: (response: MediaResponse) => void;
  halfPreview?: boolean;
  imageType: string;
  imageName?: string;
  clientIntegrationId?: string | null;
  configurationId?: string | null;
}

export interface ManageContentUrlOverlayProps {
  action: string;
  configuration: Configuration;
  onClose: () => void;
  onSave: (payload: Partial<AttractLoop>) => void;
}

export interface ManageImageOverlayProps {
  action: string;
  clientIntegrationId?: string | null;
  configurationId?: string | null;
  halfPreview?: boolean;
  mediaRecord?: MediaResponse;
  mediaType: MediaTypes;
  onClose: () => void;
  resourceName?: string;
}

export interface ManageVideoOverlayProps {
  action: string;
  configuration: Configuration;
  onClose: () => void;
}

export interface PreviewImageOverlayProps {
  imageSrc: string;
  onClose: () => void;
}
